import React, { useState } from 'react';
import { Pencil, Check, Globe, ExternalLink } from 'lucide-react';
import { UserProfile } from '../../types';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface CompanyWebsiteSectionProps {
  profile: UserProfile | null;
  onProfileUpdate: (profile: UserProfile | null) => void;
  onSave: (updatedProfile?: UserProfile | null) => Promise<boolean>;
}

export const CompanyWebsiteSection: React.FC<CompanyWebsiteSectionProps> = ({
  profile,
  onProfileUpdate,
  onSave
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempWebsite, setTempWebsite] = useState('');
  const [isSavingWebsite, setIsSavingWebsite] = useState(false);

  const handleEditClick = () => {
    setTempWebsite(profile?.company_website || '');
    setIsEditing(true);
  };

  const handleSaveWebsite = async () => {
    if (!profile) return;

    setIsSavingWebsite(true);
    const previousWebsite = profile.company_website;

    try {
      const updatedProfile = {
        ...profile,
        company_website: tempWebsite.trim()
      };

      onProfileUpdate(updatedProfile);
      const success = await onSave(updatedProfile);

      if (success) {
        setIsEditing(false);
      } else {
        onProfileUpdate({ ...profile, company_website: previousWebsite });
        setTempWebsite(previousWebsite || '');
      }
    } catch (error) {
      console.error('Error saving website:', error);
      onProfileUpdate({ ...profile, company_website: previousWebsite });
      setTempWebsite(previousWebsite || '');
    } finally {
      setIsSavingWebsite(false);
    }
  };

  const handleCancel = () => {
    setTempWebsite('');
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Card padding="md" className="bg-neutral-50">
        <label className="block text-[11px] font-medium mb-2 uppercase tracking-wide text-neutral-500">
          Company Website
        </label>
        <Input
          type="url"
          value={tempWebsite}
          onChange={(e) => setTempWebsite(e.target.value)}
          placeholder="https://yourcompany.com"
          autoFocus
          fullWidth
          inputSize="sm"
          className="mb-3"
        />
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSaveWebsite}
            disabled={isSavingWebsite}
            loading={isSavingWebsite}
            leftIcon={!isSavingWebsite ? <Check className="w-3 h-3" /> : undefined}
          >
            Save
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="md" className="bg-neutral-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-neutral-200">
            <Globe className="w-4 h-4 text-neutral-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide mb-0.5 text-neutral-400">
              Website
            </p>
            {profile?.company_website ? (
              <a
                href={profile.company_website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13px] transition-colors flex items-center gap-1 text-neutral-900 hover:text-primary-600"
              >
                {profile.company_website.replace(/^https?:\/\//, '')}
                <ExternalLink className="w-3 h-3 text-neutral-400" />
              </a>
            ) : (
              <span className="text-[13px] italic text-neutral-400">Not set</span>
            )}
          </div>
        </div>
        <Button
          id="edit-company-website-button"
          variant="ghost"
          size="sm"
          onClick={handleEditClick}
          aria-label="Edit company website"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
      </div>
    </Card>
  );
};
